import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { buildWhatsAppLink, IMAGES } from "@/lib/site";

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
};

export const Hero = () => {
  return (
    <section
      id="top"
      className="relative pt-28 md:pt-32 pb-20 md:pb-28 overflow-hidden bg-grain"
      data-testid="hero-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-6 relative z-10">
          <motion.div
            {...fadeUp}
            className="flex items-start gap-2 text-[11px] sm:text-xs uppercase tracking-[0.16em] sm:tracking-[0.22em] text-sage font-medium"
          >
            <Sparkles size={14} strokeWidth={1.5} className="mt-[2px] shrink-0" />
            <span className="leading-[1.6]">Prasava Raksha · Boutique Postnatal Retreat</span>
          </motion.div>

          <motion.h1
            {...fadeUp}
            transition={{ duration: 0.9, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-charcoal"
            data-testid="hero-headline"
          >
            Ayurvedic{" "}
            <span className="italic text-sage-700">Prasava Raksha</span>
            {" "}& Postnatal Care in Shoranur, Kerala.
          </motion.h1>

          <motion.p
            {...fadeUp}
            transition={{ duration: 0.9, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
            className="mt-5 font-serif italic text-xl md:text-2xl text-sage-700 leading-snug"
            data-testid="hero-tagline"
          >
            Healing for Mothers, Happiness for Babies.
          </motion.p>

          <motion.p
            {...fadeUp}
            transition={{ duration: 0.9, delay: 0.16, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 text-lg leading-relaxed text-charcoal/70 max-w-xl"
            data-testid="hero-subhead"
          >
            Kakkanat is a boutique Ayurvedic Prasava Raksha (Prasavaraksha)
            retreat offering authentic postnatal care in Shoranur, Palakkad.
            Our doctor-led post delivery care, with just three private villas,
            holds every mother in a calm that feels like home.
          </motion.p>

          <motion.div
            {...fadeUp}
            transition={{ duration: 0.9, delay: 0.24, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <a
              href={buildWhatsAppLink("availability")}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center justify-center gap-2 bg-charcoal text-ivory px-8 py-4 rounded-full text-sm font-medium hover:bg-sage-700 transition-colors"
              data-testid="hero-cta-availability"
            >
              Check Availability
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
            </a>
          </motion.div>

          <motion.div
            {...fadeUp}
            transition={{ duration: 0.9, delay: 0.32, ease: [0.22, 1, 0.36, 1] }}
            className="mt-12 flex items-center gap-6 text-xs text-charcoal/55"
          >
            <span className="inline-flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-sage" /> Doctor-led
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-sage" /> 3 Private Villas
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-sage" /> Kerala
            </span>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-6 relative"
        >
          <div className="relative h-64 sm:h-80 lg:h-auto lg:aspect-[4/5] w-full rounded-3xl overflow-hidden">
            <img
              src={IMAGES.hero}
              alt="A serene mother holding her newborn baby at Kakkanat, an Ayurvedic postnatal care retreat in Kerala"
              className="w-full h-full object-cover"
              data-testid="hero-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-charcoal/30 via-transparent to-transparent" />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="hidden md:block absolute -bottom-8 -left-8 bg-ivory border border-sage/15 rounded-2xl p-5 max-w-[260px] shadow-[0_8px_30px_rgba(30,30,30,0.06)]"
            data-testid="hero-floating-card"
          >
            <div className="text-xs uppercase tracking-[0.18em] text-sage">Currently</div>
            <div className="mt-1 font-serif text-lg text-charcoal leading-snug">
              Booking openings for the next quarter
            </div>
            <div className="mt-3 text-xs text-charcoal/60">
              Only 3 villas, often booked months in advance.
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* decorative ornament */}
      <div className="pointer-events-none absolute -top-20 -right-20 w-[420px] h-[420px] rounded-full bg-sage/5 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 -left-32 w-[360px] h-[360px] rounded-full bg-sand blur-3xl opacity-70" />
    </section>
  );
};

export default Hero;
